import React, { ReactElement, useEffect } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import UiKit from '../../../../../shared/ui/ui-kit';
import { colorsPalette } from '../../../../../shared/ui/styles/colors-palette';
import { SubmittingStatus } from '../../../../../shared/infrastructure/types/common/common-types';
import { Trans, useTranslation } from 'react-i18next';
import { useUILogic } from '../../../shared/providers/UILogicProvider/hooks';

interface InvitationFormProps {
  invitationToken?: string;
  email?: string;
}

let InvitationForm = function ({ invitationToken, email }: InvitationFormProps): ReactElement {
  const { t } = useTranslation();
  const { authorizationUiModule } = useUILogic();
  const coreManager = authorizationUiModule.invitationUIManager.coreManager;
  const showValidation = coreManager.formStatus === SubmittingStatus.ERROR_SUBMITTING;

  useEffect(() => {
    coreManager.initInvitation(invitationToken as string, email as LoginEmail);
  }, []);

  const passwordError =
    coreManager.passwordServerError ??
    (showValidation && !coreManager.validator.password.isValid
      ? t(coreManager.validator.password.errors[0])
      : undefined);
  const passwordConfirmationError =
    coreManager.passwordConfirmationServerError ??
    (showValidation && !coreManager.validator.passwordConfirmation.isValid
      ? t(coreManager.validator.passwordConfirmation.errors[0])
      : undefined);

  return (
    <Wrapper>
      <Note>
        <Trans
          i18nKey="invitation.note.assign"
          values={{ email: coreManager.email ?? '' }}
          components={{ strong: <UiKit.Text bold={true} /> }}
        />
      </Note>
      {coreManager.globalError && <GlobalError>{t(coreManager.globalError)}</GlobalError>}
      <FieldsGroup>
        <UiKit.form.Item.VerticalLayoutTemplate label={t('invitation.label.email')}>
          <UiKit.form.Validation
            validateStatus={coreManager.emailServerError ? 'error' : ''}
            message={coreManager.emailServerError ?? ''}
            turnValidation={!!coreManager.emailServerError}
          >
            <UiKit.Input value={coreManager.email ?? ''} disabled={true} />
          </UiKit.form.Validation>
        </UiKit.form.Item.VerticalLayoutTemplate>
        <UiKit.form.Item.VerticalLayoutTemplate label={t('invitation.label.password')}>
          <UiKit.form.Validation
            validateStatus={passwordError ? 'error' : ''}
            message={passwordError as string}
            turnValidation={!!passwordError}
          >
            <UiKit.Input
              value={coreManager.newPassword}
              onChange={value => coreManager.setNewPassword(value as LoginPassword)}
              type="password"
              showPasswordVisibilityToggle={true}
            />
          </UiKit.form.Validation>
        </UiKit.form.Item.VerticalLayoutTemplate>
        <UiKit.form.Item.VerticalLayoutTemplate label={t('invitation.label.passwordConfirmation')}>
          <UiKit.form.Validation
            validateStatus={passwordConfirmationError ? 'error' : ''}
            message={passwordConfirmationError as string}
            turnValidation={!!passwordConfirmationError}
          >
            <UiKit.Input
              value={coreManager.passwordConfirmation}
              onChange={value => coreManager.setPasswordConfirmation(value as LoginPassword)}
              type="password"
              showPasswordVisibilityToggle={true}
            />
          </UiKit.form.Validation>
        </UiKit.form.Item.VerticalLayoutTemplate>
      </FieldsGroup>
      <UiKit.Button
        loading={coreManager.formStatus === SubmittingStatus.SUBMITTING}
        disabled={!coreManager.newPassword}
        customStyles={{ width: '100%' }}
        type="primary"
        onClick={() => coreManager.submit()}
      >
        {t('invitation.submit.button')}
      </UiKit.Button>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;
const Note = styled.div`
  font-size: 14px;
  line-height: 22px;
  color: rgba(0, 0, 0, 0.88);
  text-align: left;
`;
const GlobalError = styled.div`
  font-size: 14px;
  color: ${colorsPalette.red600};
  text-align: left;
`;
const FieldsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

InvitationForm = observer(InvitationForm);

export { InvitationForm };
